import React, { useContext } from "react";
import { Menu, Button, Icon, MenuItem } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import { ViralAnnotationTrack } from "../viral-track";
import { ViralAnnotationTrackLegend } from "../viral-track/legend";
import { ViralAnnotationTable } from "../viral-annotation-table";
import classNames from "classnames";
import { StoresContext } from "../../pages/_app";
import { observer } from "mobx-react";
import {
  getViralSubstitutionMarker,
  getViralDeletionMarker,
  getViralInsertionMarker,
} from "../../utils/polygons";

export const ViralAnnotations = observer(() => {
  const rootStore = useContext(StoresContext);
  const { viralVariantStore, annotationStore } = rootStore;

  const genome = annotationStore.genome!;
  const assemblyUuid = genome.primary_assembly?.uuid;

  const { isComparison } = annotationStore;

  function renderVariantRunsDropdown() {
    const searchResults = viralVariantStore.searchResults;
    const variantUuid = viralVariantStore.selectedVariantRunUuid || Object.keys(searchResults)[0];

    if (!variantUuid) {
      return;
    }

    const selectedVariant = searchResults[variantUuid].referenceAssembly.genome;
    const menuItems = Object.keys(searchResults).map((k, idx) => {
      const data = searchResults[k];

      function selectVariant() {
        viralVariantStore.setSelectedVariantRun(k);
      }

      return (
        <MenuItem
          key={idx}
          text={data.referenceAssembly.genome.display_name}
          onClick={() => selectVariant()}
        />
      );
    });

    return (
      <div>
        <div className="comparison-text">Compare Against:</div>
        <Popover2 content={<Menu>{menuItems}</Menu>}>
          <Button text={selectedVariant.display_name} />
        </Popover2>
        {selectedVariant.external_url && (
          <a target="_blank" rel="noopener noreferrer" href={selectedVariant.external_url}>
            <Icon icon="share" className="atcc-link--icon" />
          </a>
        )}
      </div>
    );
  }

  const classes = classNames("genomes-annotations", {
    "genomes-annotations--comparison": isComparison,
  });

  const markerBaseY = 20;
  const markerBaseOffset = 7;

  function getMarker(markerPoints: string) {
    const classes2 = classNames(["annotation-polygon", "variant-polygon"]);
    return (
      <polygon
        points={markerPoints}
        style={{
          fill: "lightgray",
          opacity: 0.8,
        }}
        className={classes2}
      ></polygon>
    );
  }

  return (
    <div className={classes}>
      <div>
        <Popover2
          className="legend-popover"
          position="bottom-right"
          content={<ViralAnnotationTrackLegend />}
        >
          <Button rightIcon="caret-down" text="Annotation Legend" />
        </Popover2>
        <div className="flex">
          <div className="flex align-items-center margin-left-auto">
            <svg style={{ width: "20px", height: "40px", marginRight: "5px" }}>
              {getMarker(getViralSubstitutionMarker(2, markerBaseY, 6))}
            </svg>
            Substitution
          </div>
          <div className="flex align-items-center margin-left">
            <svg style={{ width: "20px", height: "40px", marginRight: "5px" }}>
              {getMarker(getViralDeletionMarker(2, markerBaseY - 3, markerBaseOffset))} deletion
            </svg>
            Deletion
          </div>
          <div className="flex align-items-center margin-left">
            <svg style={{ width: "20px", height: "40px", marginRight: "5px" }}>
              {getMarker(getViralInsertionMarker(2, markerBaseY + 3, markerBaseOffset))} insertion
            </svg>
            Insertion
          </div>
        </div>
      </div>
      <ViralAnnotationTrack
        annotationSetUuid={viralVariantStore.selectedAnnotationSetUuid!}
        assemblyUuid={assemblyUuid}
      />
      <div className="genomes-annotations__container">
        <div className="genomes-annotations__filter">{renderVariantRunsDropdown()}</div>
      </div>
      <ViralAnnotationTable />
    </div>
  );
});
